import React, { createContext, useContext, useEffect, useRef, useState } from "react";

/* The cast, recoloured from the reference design onto the AutoPilot palette:
   indigo primary, slate, sky and amber accents on the dark command-center background. */
const INDIGO = "#6366F1";
const SLATE = "#1E293B";
const SKY = "#38BDF8";
const AMBER = "#FBBF24";
const INK = "#0B0F17";

/* One pointer listener for the whole stage instead of one per eye. */
const MouseContext = createContext({ x: 0, y: 0 });

const useMouse = () => useContext(MouseContext);

const useLook = (ref, maxDistance, forceX, forceY) => {
  const { x: mouseX, y: mouseY } = useMouse();
  if (forceX !== undefined && forceY !== undefined) return { x: forceX, y: forceY };
  if (!ref.current) return { x: 0, y: 0 };
  const box = ref.current.getBoundingClientRect();
  const deltaX = mouseX - (box.left + box.width / 2);
  const deltaY = mouseY - (box.top + box.height / 2);
  const distance = Math.min(Math.sqrt(deltaX ** 2 + deltaY ** 2), maxDistance);
  const angle = Math.atan2(deltaY, deltaX);
  return { x: Math.cos(angle) * distance, y: Math.sin(angle) * distance };
};

const Pupil = ({ size = 12, maxDistance = 5, pupilColor = INK, forceLookX, forceLookY }) => {
  const ref = useRef(null);
  const pos = useLook(ref, maxDistance, forceLookX, forceLookY);
  return (
    <div
      ref={ref}
      className="rounded-full"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: pupilColor,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        transition: "transform 0.1s ease-out",
      }}
    />
  );
};

const EyeBall = ({
  size = 18,
  pupilSize = 7,
  maxDistance = 5,
  eyeColor = "#F8FAFC",
  pupilColor = INK,
  isBlinking = false,
  forceLookX,
  forceLookY,
}) => {
  const ref = useRef(null);
  const pos = useLook(ref, maxDistance, forceLookX, forceLookY);
  return (
    <div
      ref={ref}
      className="rounded-full flex items-center justify-center transition-all duration-150"
      style={{
        width: `${size}px`,
        height: isBlinking ? "2px" : `${size}px`,
        backgroundColor: eyeColor,
        overflow: "hidden",
      }}
    >
      {!isBlinking && (
        <div
          className="rounded-full"
          style={{
            width: `${pupilSize}px`,
            height: `${pupilSize}px`,
            backgroundColor: pupilColor,
            transform: `translate(${pos.x}px, ${pos.y}px)`,
            transition: "transform 0.1s ease-out",
          }}
        />
      )}
    </div>
  );
};

/* Random, independent blinks so the two faces never blink in lockstep. */
const useBlink = () => {
  const [blinking, setBlinking] = useState(false);
  useEffect(() => {
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setBlinking(true);
        timer = setTimeout(() => {
          setBlinking(false);
          schedule();
        }, 150);
      }, Math.random() * 4000 + 3000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);
  return blinking;
};

/**
 * The animated cast.
 *
 * typing        - a field has focus: they glance at each other, then back.
 * secretLength  - characters typed into a masked field: the tall one cranes over to peek.
 * secretVisible - the secret is on screen: everyone politely looks away.
 */
export const CharacterStage = ({ typing = false, secretLength = 0, secretVisible = false }) => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [glancing, setGlancing] = useState(false);
  const [peeking, setPeeking] = useState(false);

  const indigoRef = useRef(null);
  const slateRef = useRef(null);
  const skyRef = useRef(null);
  const amberRef = useRef(null);

  const indigoBlink = useBlink();
  const slateBlink = useBlink();

  useEffect(() => {
    const onMove = (e) => setMouse({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useEffect(() => {
    if (!typing) {
      setGlancing(false);
      return undefined;
    }
    setGlancing(true);
    const timer = setTimeout(() => setGlancing(false), 800);
    return () => clearTimeout(timer);
  }, [typing]);

  /* While the secret is visible they look away - but the tall one keeps sneaking a look. */
  useEffect(() => {
    if (!(secretLength > 0 && secretVisible)) {
      setPeeking(false);
      return undefined;
    }
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setPeeking(true);
        timer = setTimeout(() => {
          setPeeking(false);
          schedule();
        }, 800);
      }, Math.random() * 3000 + 2000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [secretLength, secretVisible]);

  const posture = (ref) => {
    if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };
    const box = ref.current.getBoundingClientRect();
    const deltaX = mouse.x - (box.left + box.width / 2);
    const deltaY = mouse.y - (box.top + box.height / 3);
    return {
      faceX: Math.max(-15, Math.min(15, deltaX / 20)),
      faceY: Math.max(-10, Math.min(10, deltaY / 30)),
      bodySkew: Math.max(-6, Math.min(6, -deltaX / 120)),
    };
  };

  const indigo = posture(indigoRef);
  const slate = posture(slateRef);
  const sky = posture(skyRef);
  const amber = posture(amberRef);

  const lookAway = secretLength > 0 && secretVisible;
  const craning = typing || (secretLength > 0 && !secretVisible);

  return (
    <MouseContext.Provider value={mouse}>
      <div className="relative" style={{ width: "550px", height: "400px" }}>
        {/* Indigo - tallest, back layer. The nosy one. */}
        <div
          ref={indigoRef}
          className="absolute bottom-0 transition-all duration-700 ease-in-out"
          style={{
            left: "70px",
            width: "180px",
            height: craning ? "440px" : "400px",
            backgroundColor: INDIGO,
            borderRadius: "10px 10px 0 0",
            zIndex: 1,
            boxShadow: "0 0 90px rgba(99,102,241,0.35)",
            transform: lookAway
              ? "skewX(0deg)"
              : craning
                ? `skewX(${indigo.bodySkew - 12}deg) translateX(40px)`
                : `skewX(${indigo.bodySkew}deg)`,
            transformOrigin: "bottom center",
          }}
        >
          <div
            className="absolute flex gap-8 transition-all duration-700 ease-in-out"
            style={{
              left: lookAway ? "20px" : glancing ? "55px" : `${45 + indigo.faceX}px`,
              top: lookAway ? "35px" : glancing ? "65px" : `${40 + indigo.faceY}px`,
            }}
          >
            {[0, 1].map((i) => (
              <EyeBall
                key={i}
                size={18}
                pupilSize={7}
                isBlinking={indigoBlink}
                forceLookX={lookAway ? (peeking ? 4 : -4) : glancing ? 3 : undefined}
                forceLookY={lookAway ? (peeking ? 5 : -4) : glancing ? 4 : undefined}
              />
            ))}
          </div>
        </div>

        {/* Slate - the dark one in the middle. */}
        <div
          ref={slateRef}
          className="absolute bottom-0 transition-all duration-700 ease-in-out"
          style={{
            left: "240px",
            width: "120px",
            height: "310px",
            backgroundColor: SLATE,
            borderRadius: "8px 8px 0 0",
            zIndex: 2,
            transform: lookAway
              ? "skewX(0deg)"
              : glancing
                ? `skewX(${slate.bodySkew * 1.5 + 10}deg) translateX(20px)`
                : `skewX(${slate.bodySkew * (craning ? 1.5 : 1)}deg)`,
            transformOrigin: "bottom center",
          }}
        >
          <div
            className="absolute flex gap-6 transition-all duration-700 ease-in-out"
            style={{
              left: lookAway ? "10px" : glancing ? "32px" : `${26 + slate.faceX}px`,
              top: lookAway ? "28px" : glancing ? "12px" : `${32 + slate.faceY}px`,
            }}
          >
            {[0, 1].map((i) => (
              <EyeBall
                key={i}
                size={16}
                pupilSize={6}
                maxDistance={4}
                isBlinking={slateBlink}
                forceLookX={lookAway ? -4 : glancing ? 0 : undefined}
                forceLookY={lookAway ? -4 : glancing ? -4 : undefined}
              />
            ))}
          </div>
        </div>

        {/* Sky - the dome, front left. */}
        <div
          ref={skyRef}
          className="absolute bottom-0 transition-all duration-700 ease-in-out"
          style={{
            left: "0px",
            width: "240px",
            height: "200px",
            zIndex: 3,
            backgroundColor: SKY,
            borderRadius: "120px 120px 0 0",
            transform: lookAway ? "skewX(0deg)" : `skewX(${sky.bodySkew}deg)`,
            transformOrigin: "bottom center",
          }}
        >
          <div
            className="absolute flex gap-8 transition-all duration-200 ease-out"
            style={{
              left: lookAway ? "50px" : `${82 + sky.faceX}px`,
              top: lookAway ? "85px" : `${90 + sky.faceY}px`,
            }}
          >
            {[0, 1].map((i) => (
              <Pupil key={i} forceLookX={lookAway ? -5 : undefined} forceLookY={lookAway ? -4 : undefined} />
            ))}
          </div>
        </div>

        {/* Amber - front right, the only one with a mouth. */}
        <div
          ref={amberRef}
          className="absolute bottom-0 transition-all duration-700 ease-in-out"
          style={{
            left: "310px",
            width: "140px",
            height: "230px",
            backgroundColor: AMBER,
            borderRadius: "70px 70px 0 0",
            zIndex: 4,
            transform: lookAway ? "skewX(0deg)" : `skewX(${amber.bodySkew}deg)`,
            transformOrigin: "bottom center",
          }}
        >
          <div
            className="absolute flex gap-6 transition-all duration-200 ease-out"
            style={{
              left: lookAway ? "20px" : `${52 + amber.faceX}px`,
              top: lookAway ? "35px" : `${40 + amber.faceY}px`,
            }}
          >
            {[0, 1].map((i) => (
              <Pupil key={i} forceLookX={lookAway ? -5 : undefined} forceLookY={lookAway ? -4 : undefined} />
            ))}
          </div>
          <div
            className="absolute w-20 h-[4px] rounded-full transition-all duration-200 ease-out"
            style={{
              backgroundColor: INK,
              left: lookAway ? "10px" : `${40 + amber.faceX}px`,
              top: lookAway ? "88px" : `${88 + amber.faceY}px`,
            }}
          />
        </div>
      </div>
    </MouseContext.Provider>
  );
};

export default CharacterStage;
